import { cloneElement, useEffect, useState } from 'react'
import GuestLearn from '@assets/images/GuestLearn.png'
import GuestTeach from '@assets/images/GuestTeach.png'
import { ProgressViewContainer } from '@features/event/components/create-event-modal/create-event-modal.styles'
import OnboardingModal from '@features/onboarding/components'
import { useTheme } from '@features/theme/theme.hooks'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useLazyClassroomGetCurrentClassroomQuery } from '@app/services/auto-generated/classroom'
import {
  useModulesGetUserModulesQuery,
  useModulesSubmitModuleResultMutation,
} from '@app/services/auto-generated/modules'
import { Box, Button, Container, Modal, ProgressView, Stack, Typography } from '@common/components'
import { PRICING_ROUTE } from '@common/constants/routes'
import { useToggle, useTranslation } from '@common/hooks'
import {
  Card,
  CardActionArea,
  ImageInnerContainer,
  ImageOuterContainer,
  TitleContainer,
  TitleTypography,
} from './modules-checker.styles'

interface ModuleFormData {
  learning: Array<{ language: string; proficiency: number }>
  status: 'STUDENT' | 'TEACHER'
}

const ModulesChecker = () => {
  const { data: modules, refetch } = useModulesGetUserModulesQuery()
  const [sendModule] = useModulesSubmitModuleResultMutation()
  const [getCurrentClassroom] = useLazyClassroomGetCurrentClassroomQuery()

  const { open, handleOpen, handleClose } = useToggle(false)
  const [currentStep, setCurrentStep] = useState(0)
  const navigate = useNavigate()
  const handleNextStep = () => {
    setCurrentStep(currentStep + 1)
  }

  useEffect(() => {
    if (
      modules &&
      modules.required &&
      modules.required.length > 0 &&
      !modules.required.includes('CollectInformationModule')
    ) {
      handleOpen()
    }
  }, [modules])

  const { handleSubmit, setValue } = useForm<ModuleFormData>({
    defaultValues: {
      learning: [],
      status: 'STUDENT',
    },
  })

  const onSubmit: SubmitHandler<ModuleFormData> = async (formState) => {
    try {
      if (modules?.required.includes('TeacherStudentQuestionnaire')) {
        await sendModule({
          moduleResult: {
            id: 'TeacherStudentQuestionnaire',
            data: { status: formState.status },
          },
        }).unwrap()
      }
      if (modules?.required.includes('LanguageQuestionnaire')) {
        await sendModule({
          moduleResult: {
            id: 'LanguageQuestionnaire',
            data: {
              speaking: [{ language: 'en', proficiency: 4 }],
              learning: [{ language: 'en', proficiency: 4 }],
            },
          },
        }).unwrap()
      }
      if (formState.status === 'TEACHER') {
        navigate(PRICING_ROUTE)
      }
      await getCurrentClassroom()
      await refetch()
      handleClose()
    } catch (err) {
      console.error(err)
    }
  }

  const steps = () => {
    const arrayOfSteps = []
    if (modules?.required.includes('TeacherStudentQuestionnaire')) {
      arrayOfSteps.push(<StatusStep key={0} handleNextStep={handleNextStep} setValue={setValue} />)
    }
    // if (modules?.required.includes('LanguageQuestionnaire')) {
    //   arrayOfSteps.push(
    //     <LanguageStep
    //       key={1}
    //       getValues={getValues}
    //       handleNextStep={handleNextStep}
    //       setValue={setValue}
    //     />,
    //   )
    // }
    return arrayOfSteps
  }

  if (modules?.required.includes('CollectInformationModule')) {
    return <OnboardingModal />
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      paperProps={{
        sx: { width: '900px', height: '500px', maxHeight: '90vh' },
      }}
    >
      <ProgressViewContainer as='form' onSubmit={handleSubmit(onSubmit)} sx={{ height: '100vh' }}>
        <ProgressView
          currentStep={currentStep}
          showSteps={false}
          swipeable={true}
          showProgressBar={true}
        >
          {steps().map((step, index) => {
            return cloneElement(step, { isLastStep: index === steps().length - 1 })
          })}
        </ProgressView>
      </ProgressViewContainer>
    </Modal>
  )
}
export default ModulesChecker

interface StatusStepProps {
  handleNextStep: () => void
  setValue: (field: keyof ModuleFormData, value: ModuleFormData[keyof ModuleFormData]) => void
  isLastStep?: boolean
}

interface RegisterCardProps {
  isTeach: boolean
  onClick: () => void
  selected: boolean
}

const StatusStep = (props: StatusStepProps) => {
  const { handleNextStep, setValue, isLastStep } = props
  const [selectedCard, setSelectedCard] = useState(0)
  const { t } = useTranslation()
  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', padding: '50px' }}>
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box>
          <Typography variant='h5' fontWeight={400} align='center' sx={{ mb: 3 }}>
            {t('login.loginAsGuest.stp1.action', "I'm here to ...")}
          </Typography>
          <Stack direction='row' spacing={3}>
            <RegisterCard
              isTeach={true}
              selected={selectedCard === 1}
              onClick={() => {
                setValue('status', 'TEACHER')
                setSelectedCard(1)
              }}
            />
            <RegisterCard
              isTeach={false}
              selected={selectedCard === 2}
              onClick={() => {
                setValue('status', 'STUDENT')
                setSelectedCard(2)
              }}
            />
          </Stack>
        </Box>
      </Box>
      <Button
        onClick={!isLastStep ? handleNextStep : undefined}
        type={isLastStep ? 'submit' : 'button'}
        variant='contained'
        sx={{ marginTop: '50px', width: '100px', marginLeft: 'auto', marginRight: 'auto' }}
        disabled={selectedCard === 0}
      >
        {' '}
        {isLastStep ? 'Submit' : 'Next'}
      </Button>
    </Container>
  )
}

// const LanguageStep = (props) => {
//   const { setValue, handleNextStep, getValues, isLastStep } = props
//   const { languages } = useLanguages()
//   const { t } = useTranslation()
//   const isTeacher = getValues('status') === 'teacher'
//   const handleChange = (e: React.ChangeEvent, value: any): void => {
//     setValue('learning', [{ language: value?.code, proficiency: 4 }])
//     handleNextStep()
//   }
//   const [isSelected, setIsSelected] = useState(0)
//   return (
//     <Box sx={{ padding: '50px', display: 'flex', flexDirection: 'column' }}>
//       <Container
//         sx={{
//           height: '100%',
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'center',
//         }}
//       >
//         <Box>
//           <Typography sx={{ fontWeight: '500', fontSize: '22px' }}>
//             {isTeacher
//               ? t('register.teaching-language-question', 'What language are you teaching ?')
//               : t('register.native-language-question', 'What is your native language ?')}
//           </Typography>
//           <FlagsContainer
//             sx={{
//               m: 2,
//               maxWidth: '450px',
//             }}
//           >
//             <>
//               {langs.map((elem, key) => (
//                 <Flag
//                   key={key}
//                   isSelected={key === isSelected ? true : false}
//                   component='img'
//                   sx={{
//                     width: '75px',
//                     boxShadow: '0px 0px 5px 1px rgba(0,0,0,0.35)',
//                     marginTop: '10px',
//                   }}
//                   src={elem.flag_image}
//                   alt={elem.name}
//                   onClick={() => {
//                     setValue('learning', [{ language: elem.code, proficiency: 4 }])
//                     setIsSelected(key)
//                   }}
//                 />
//               ))}
//             </>
//           </FlagsContainer>
//           <Autocomplete
//             options={langs}
//             // defaultValue={[]}
//             getOptionLabel={(option: any) => option.name ?? ''}
//             renderOption={(props, option, state) => (
//               <Box component='li' {...props}>
//                 <Typography sx={{ flex: 1 }}>{option.name}</Typography>
//                 <img
//                   src={option.flag_image}
//                   alt={option.name}
//                   style={{ width: '25px', borderRadius: '2px' }}
//                 />
//               </Box>
//             )}
//             renderInput={(params: any) => {
//               return <TextField {...params} placeholder='Other languages' size='small' />
//             }}
//             size='small'
//             onChange={handleChange}
//           />
//         </Box>
//       </Container>
//       <Button
//         onClick={!isLastStep ? handleNextStep : undefined}
//         type={isLastStep ? 'submit' : 'button'}
//         sx={{ marginTop: '50px', marginLeft: 'auto', marginRight: 'auto', width: '100px' }}
//         variant='contained'
//       >
//         {' '}
//         {isLastStep ? 'Submit' : 'Next'}
//       </Button>
//     </Box>
//   )
// }

const RegisterCard = (props: RegisterCardProps) => {
  const { isTeach, onClick, selected } = props
  const { t } = useTranslation()
  const { isLight } = useTheme()
  const cardStyles = {
    border: selected ? (isLight ? '4px solid #0000008a' : '4px solid #C5C6D0') : 'none',
    borderRadius: '20px',
  }
  const handleCardClick = () => {
    onClick()
  }
  return (
    <Card>
      <CardActionArea onClick={handleCardClick} style={cardStyles}>
        <ImageOuterContainer isTeach={isTeach}>
          <ImageInnerContainer isTeach={isTeach}>
            <img src={isTeach ? GuestTeach : GuestLearn} alt={'learnLogo'} />
          </ImageInnerContainer>
        </ImageOuterContainer>
        <TitleContainer>
          <TitleTypography>
            {isTeach
              ? t('login.loginAsGuestCards.teach', 'Teach')
              : t('login.loginAsGuestCards.learn', 'Learn')}
          </TitleTypography>
        </TitleContainer>
      </CardActionArea>
    </Card>
  )
}

// const langs = [
//   {
//     name: 'English',
//     flag_image: 'https://flagicons.lipis.dev/flags/4x3/gb.svg',
//     code: 'en',
//   },
//   {
//     name: 'French',
//     flag_image: 'https://flagicons.lipis.dev/flags/4x3/fr.svg',
//     code: 'fr',
//   },
//   {
//     name: 'German',
//     flag_image: 'https://flagicons.lipis.dev/flags/4x3/de.svg',
//     code: 'de',
//   },
//   {
//     name: 'Italian',
//     flag_image: 'https://flagicons.lipis.dev/flags/4x3/it.svg',
//     code: 'it',
//   },
//   {
//     name: 'Spanish',
//     flag_image: 'https://flagicons.lipis.dev/flags/4x3/es.svg',
//     code: 'es',
//   },
//   {
//     name: 'Portuguese',
//     flag_image: 'https://flagicons.lipis.dev/flags/4x3/br.svg',
//     code: 'pt',
//   },
//   {
//     name: 'Russian',
//     flag_image: 'https://flagicons.lipis.dev/flags/4x3/ru.svg',
//     code: 'ru',
//   },
//   {
//     name: 'Arabic',
//     flag_image: 'https://flagicons.lipis.dev/flags/4x3/tn.svg',
//     code: 'ar',
//   },
//   {
//     name: 'Japanese',
//     flag_image: 'https://flagicons.lipis.dev/flags/4x3/jp.svg',
//     code: 'ja',
//   },
//   {
//     name: 'Chinese',
//     flag_image: 'https://flagicons.lipis.dev/flags/4x3/cn.svg',
//     code: 'zh-CN',
//   },
//   // {
//   //   name: "Korean",
//   //   flag_image: "https://flagicons.lipis.dev/flags/4x3/kr.svg",
//   //   code: "ko",
//   // },
// ]
